import * as z from "zod";

export const PATH_PLANNING_CORE_STEPS = [
  "process_slices",
  "tool_axes",
  "optional_moves",
  "add_links",
  "auxiliary_values",
] as const;

export const PATH_PLANNING_ADHOC_STEPS = [] as const;

export const PATH_PLANNING_ALL_STEPS = [
  ...PATH_PLANNING_CORE_STEPS,
  ...PATH_PLANNING_ADHOC_STEPS,
] as const;

export const pathPlanningStepSchema = z.enum(PATH_PLANNING_ALL_STEPS);
export type PathPlanningStep = z.infer<typeof pathPlanningStepSchema>;

export const pathPlanningStepStatusSchema = z.enum(["idle", "running", "success", "error"]);
export type PathPlanningStepStatus = z.infer<typeof pathPlanningStepStatusSchema>;

export type PathPlanningWorkflowStatus = "not_started" | "running" | "published";

export const STEP_GLB_TTL_MS = 3_600_000; // 1 hour — matches Azure Blob Storage signed URL TTL

export function glbSignatureHasExpired({
  glb,
  now,
  ttlMs = STEP_GLB_TTL_MS,
}: {
  glb?: { stored_at: string } | null;
  now: number;
  ttlMs?: number;
}): boolean {
  if (!glb) return false;
  const storedAt = new Date(glb.stored_at).getTime();
  if (Number.isNaN(storedAt)) return true;
  return now - storedAt > ttlMs;
}

export const pathPlanningStepGlbSchema = z.object({
  url: z.string(),
  stored_at: z.string(), // ISO timestamp of when the URL was generated
});

export const pathPlanningPersistedStateSchema = z.object({
  workflowId: z.string().nullable(),
  runId: z.string().nullable(),
  workflowStatus: z.enum(["not_started", "running", "published"]),
  publishFileName: z.string().nullable(),
  initiatedBy: z.string().nullable().optional(),
  stepStatuses: z.record(pathPlanningStepSchema, pathPlanningStepStatusSchema),
  stepErrors: z.record(pathPlanningStepSchema, z.string().nullable()),
  stepGlbs: z.record(pathPlanningStepSchema, pathPlanningStepGlbSchema.nullable()),
});
export type PathPlanningPersistedState = z.infer<typeof pathPlanningPersistedStateSchema>;

export function getPathPlanningStepLabel(step: PathPlanningStep): string {
  const labels: Record<PathPlanningStep, string> = {
    process_slices: "Process Slices",
    tool_axes: "Generate tooltips and axes",
    optional_moves: "Optional Moves",
    add_links: "Links",
    auxiliary_values: "Path Attributes",
  };
  return labels[step] || step;
}

const insetStrategySchema = z.enum(["flat", "linear", "power", "inverse_power", "bell"]);

export const sharedPathPlanningParametersSchema = z.object({
  tool_radius: z.number().min(0.01, "Tool radius must be at least 0.01 mm"),
  sheet_thickness: z.number().min(0.01, "Sheet thickness must be at least 0.01 mm"),
  cell_id: z.string().min(1, "Cell ID is required"),
  process_enable_cull: z.boolean(),
  process_cull_min_ptcount: z.number().int().min(1),
  process_enable_adaptive_resample: z.boolean(),
  process_resample_spacing: z.number().positive(),
  process_resample_max_angle: z.number(),
  process_resample_angle_tol: z.number(),
  process_resample_do_filter: z.boolean(),
  process_resample_strength: z.number().int().positive(),
  process_enable_reduce_path: z.boolean(),
  process_reduce_tolerance: z.number().positive(),
  process_enable_alternate_layers: z.boolean(),

  tool_axes_off_axis_r1: z.number().optional(),
  tool_axes_off_axis_r2: z.number().optional(),
  tool_axes_fast_clamp_initial: z.number(),
  tool_axes_axis_filter_strength: z.number().int().nullable().optional(),
  tool_axes_clamp_normals_enabled: z.boolean(),
  tool_axes_clamp_normals_upper_bound: z.number().positive(),
  tool_axes_clamp_normals_lower_bound: z.number().optional(),
  tool_axes_clamp_normals_remap: z.boolean(),

  optional_enable_inject_domesets: z.boolean(),
  optional_inset_strategy: insetStrategySchema,
  optional_inset_set_period: z.number().int().min(1),
  optional_predome_init_frac: z.number().min(0).max(1),
  optional_predome_final_frac: z.number().min(0).max(1),
  optional_r2_retract: z.number(),
  optional_inset_flat_spacing: z.number().int().min(1),
  optional_inset_linear_slope: z.number().optional(),
  optional_inset_linear_yshift: z.number().optional(),
  optional_inset_power_power: z.number().optional(),
  optional_inset_power_xshift: z.number().int().optional(),
  optional_inset_power_yshift: z.number().int().optional(),
  optional_inset_power_boundary_offset: z.number().int().optional(),
  optional_inset_bell_c: z.number().int().optional(),
  optional_inset_bell_w: z.number().optional(),
  optional_inset_bell_s: z.number().optional(),
  optional_enable_plunges_retracts: z.boolean(),
  optional_plunge_distance: z.number().min(0),
  optional_retract_distance: z.number().min(0),
  optional_retract_angle: z.number(),

  links_dynamic_links: z.boolean(),
  links_axis_key: z.enum(["tool_axis", "tool1_axis", "tool2_axis"]),
  links_r2: z.boolean(),
  links_rotate: z.boolean(),
  links_r1_safe_plane: z.number(),
  links_r2_safe_plane: z.number(),
  links_link_dist_for_safe: z.number().min(0),

  auxiliary_dynamic_rail: z.boolean(),
  auxiliary_rail_offset: z.number(),
  auxiliary_r1_range_min: z.number().optional(),
  auxiliary_r1_range_max: z.number().optional(),
  auxiliary_r2_range_min: z.number().optional(),
  auxiliary_r2_range_max: z.number().optional(),
  auxiliary_r1_shift: z.number(),
  auxiliary_r2_shift: z.number(),
  auxiliary_set_speeds: z.boolean(),
  auxiliary_forming_speed: z.number().positive(),
  auxiliary_link_speed: z.number().positive(),
  auxiliary_plunge_speed: z.number().positive(),
  auxiliary_retract_speed: z.number().positive(),
});

export const pathPlanningFormSchema = z
  .object({
    selectedFileId: z.string().min(1, "Please select a CSV file"),
    ...sharedPathPlanningParametersSchema.shape,
  })
  .superRefine((values, context) => {
    const hasR1 = typeof values.tool_axes_off_axis_r1 === "number";
    const hasR2 = typeof values.tool_axes_off_axis_r2 === "number";

    if (hasR1 === hasR2) {
      // continue
    } else {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tool_axes_off_axis_r1"],
        message: "Provide both off-axis angles or leave both blank",
      });
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tool_axes_off_axis_r2"],
        message: "Provide both off-axis angles or leave both blank",
      });
    }

    if (values.optional_predome_init_frac > values.optional_predome_final_frac) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["optional_predome_init_frac"],
        message: "Predome start must be less than or equal to predome end",
      });
    }

    const hasRailRangeR1 =
      typeof values.auxiliary_r1_range_min === "number" ||
      typeof values.auxiliary_r1_range_max === "number";
    const hasRailRangeR2 =
      typeof values.auxiliary_r2_range_min === "number" ||
      typeof values.auxiliary_r2_range_max === "number";

    if (hasRailRangeR1) {
      if (
        typeof values.auxiliary_r1_range_min !== "number" ||
        typeof values.auxiliary_r1_range_max !== "number"
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["auxiliary_r1_range_min"],
          message: "Provide both R1 range bounds or leave both blank",
        });
      }
    }

    if (hasRailRangeR2) {
      if (
        typeof values.auxiliary_r2_range_min !== "number" ||
        typeof values.auxiliary_r2_range_max !== "number"
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["auxiliary_r2_range_min"],
          message: "Provide both R2 range bounds or leave both blank",
        });
      }
    }

    if (
      typeof values.auxiliary_r1_range_min === "number" &&
      typeof values.auxiliary_r1_range_max === "number" &&
      values.auxiliary_r1_range_min > values.auxiliary_r1_range_max
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["auxiliary_r1_range_min"],
        message: "R1 range min cannot exceed max",
      });
    }

    if (
      typeof values.auxiliary_r2_range_min === "number" &&
      typeof values.auxiliary_r2_range_max === "number" &&
      values.auxiliary_r2_range_min > values.auxiliary_r2_range_max
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["auxiliary_r2_range_min"],
        message: "R2 range min cannot exceed max",
      });
    }
  });

export type PathPlanningFormValues = z.infer<typeof pathPlanningFormSchema>;

export const PATH_PLANNING_FORM_DEFAULTS = {
  selectedFileId: "",
  process_enable_cull: true,
  process_cull_min_ptcount: 3,
  process_enable_adaptive_resample: false,
  process_resample_spacing: 1,
  process_resample_max_angle: 179.9,
  process_resample_angle_tol: 0.1,
  process_resample_do_filter: false,
  process_resample_strength: 4,
  process_enable_reduce_path: false,
  process_reduce_tolerance: 0.03,
  process_enable_alternate_layers: false,
  tool_axes_off_axis_r1: 0,
  tool_axes_off_axis_r2: 0,
  tool_axes_fast_clamp_initial: 0,
  tool_axes_axis_filter_strength: undefined,
  tool_axes_clamp_normals_enabled: false,
  tool_axes_clamp_normals_upper_bound: 60,
  tool_axes_clamp_normals_lower_bound: undefined,
  tool_axes_clamp_normals_remap: false,
  optional_enable_inject_domesets: false,
  optional_inset_strategy: "linear",
  optional_inset_set_period: 53,
  optional_predome_init_frac: 0.01,
  optional_predome_final_frac: 0.15,
  optional_r2_retract: 80,
  optional_inset_flat_spacing: 50,
  optional_inset_linear_slope: undefined,
  optional_inset_linear_yshift: undefined,
  optional_inset_power_power: undefined,
  optional_inset_power_xshift: undefined,
  optional_inset_power_yshift: undefined,
  optional_inset_power_boundary_offset: undefined,
  optional_inset_bell_c: undefined,
  optional_inset_bell_w: undefined,
  optional_inset_bell_s: undefined,
  optional_enable_plunges_retracts: false,
  optional_plunge_distance: 50,
  optional_retract_distance: 50,
  optional_retract_angle: 0,
  links_dynamic_links: true,
  links_axis_key: "tool_axis",
  links_r2: true,
  links_rotate: false,
  links_r1_safe_plane: 100,
  links_r2_safe_plane: -400,
  links_link_dist_for_safe: 5,
  auxiliary_dynamic_rail: true,
  auxiliary_rail_offset: 0,
  auxiliary_r1_range_min: undefined,
  auxiliary_r1_range_max: undefined,
  auxiliary_r2_range_min: undefined,
  auxiliary_r2_range_max: undefined,
  auxiliary_r1_shift: 0,
  auxiliary_r2_shift: 0,
  auxiliary_set_speeds: false,
  auxiliary_forming_speed: 50,
  auxiliary_link_speed: 50,
  auxiliary_plunge_speed: 5,
  auxiliary_retract_speed: 10,
} satisfies Omit<PathPlanningFormValues, "tool_radius" | "sheet_thickness" | "cell_id">;

// The slicer already resamples and reduces the path and alternates travel directions,
// which is why those process-slices ops default off above. Slices cut by the retired
// standard slicer (SW-1937) did none of that, so they turn back on for one of those.
// Saved inputs from a previous run still win.
export const LEGACY_SLICE_PROCESS_DEFAULTS = {
  process_enable_adaptive_resample: true,
  process_enable_reduce_path: true,
  process_enable_alternate_layers: true,
} satisfies Partial<PathPlanningFormValues>;

export const startPathPlanningWorkflowInputSchema = z.object({
  projectId: z.string().min(1),
  partId: z.string().min(1),
  fileId: z.string().min(1),
  fileMd5: z.string().optional(),
  procedureId: z.string().min(1),
  operationId: z.string().min(1),
  tool_radius: z.union([z.number().min(0.01), z.array(z.number().min(0.01)).min(1).max(2)]),
  sheet_thickness: z.number().min(0.01),
  cell_id: z.string().min(1),
  initiatedBy: z.string().optional(),
});

export type StartPathPlanningWorkflowInput = z.infer<typeof startPathPlanningWorkflowInputSchema>;

export const publishPathPlanningWorkflowResultSchema = z
  .object({
    attachment_id: z.string().nullable().optional(),
  })
  .passthrough();

export const startPathPlanningWorkflowResultSchema = z.object({
  workflowId: z.string().min(1),
  runId: z.string().min(1),
});

export const pathPlanningStepResultSchema = z.object({
  status: z.string(),
  type: z.string(),
  artifact_url: z.string().url().nullable().optional(),
  artifact_md5: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});

export const runPathPlanningStepsResponseSchema = z.object({
  status: z.string(),
  error: z.string().nullable().optional(),
  results: z.array(pathPlanningStepResultSchema).nullable().optional(),
});

export type PathPlanningStepResult = z.infer<typeof pathPlanningStepResultSchema>;

const processSlicesRequestSchema = z.object({
  cull: z
    .object({
      min_ptcount: z.number().int().optional(),
      cull_duplicates_by_layer: z.boolean().optional(),
      test_keys: z.array(z.string()).optional(),
      reindex: z.boolean().optional(),
      cull_tolerance: z.number().optional(),
    })
    .optional(),
  filter: z
    .object({
      keys: z.array(z.string()),
      window: z.number().int(),
      degree: z.number().int().optional(),
      wrap: z.boolean().optional(),
      closure_eps: z.number().int().optional(),
    })
    .optional(),
  resample_adaptive: z
    .object({
      points_key: z.string().optional(),
      spacing: z.number(),
      ratio: z.number().optional(),
      crv_method: z.string().optional(),
      max_angle: z.number().optional(),
      angle_tol: z.number().optional(),
      do_filter: z.boolean().optional(),
      strength: z.number().optional(),
      close: z.boolean().optional(),
    })
    .optional(),
  reduce_path: z
    .object({
      e: z.number(),
      points_key: z.string().optional(),
      cycles: z.number().int().optional(),
    })
    .optional(),
  parse_regions: z
    .object({
      slice_dir: z.array(z.number()).optional(),
      slice_key: z.string().optional(),
      parents_only: z.boolean().optional(),
      overwrite: z.boolean().optional(),
    })
    .optional(),
  alternate_layers: z
    .object({
      odd: z.boolean().optional(),
      no_check: z.boolean().optional(),
    })
    .optional(),
});

const toolAxesRequestSchema = z.object({
  cull: processSlicesRequestSchema.shape.cull,
  filter: processSlicesRequestSchema.shape.filter,
  clamp_normals: z
    .object({
      upper_bound: z.number().optional(),
      lower_bound: z.number().optional(),
      remap: z.boolean().optional(),
    })
    .optional(),
  generate_both_tips: z.object({
    off_axis: z.array(z.number()).optional(),
    fast_clamp_initial: z.number().optional(),
    sinelaw: z.boolean().optional(),
    powermill_vector: z.boolean().optional(),
    axis_filter_strength: z.number().nullable().optional(),
  }),
});

const insetParamsSchema = z.union([
  z.strictObject({ spacing: z.number().int() }),
  z.strictObject({
    slope: z.number().optional(),
    yshift: z.number().optional(),
    use_legacy_version: z.boolean().optional(),
  }),
  z.strictObject({
    power: z.number().optional(),
    xshift: z.number().int().optional(),
    yshift: z.number().int().optional(),
    boundary_offset: z.number().int().optional(),
  }),
  z.strictObject({
    c: z.number().int().optional(),
    w: z.number().optional(),
    s: z.number().optional(),
  }),
]);

const optionalMovesRequestSchema = z.object({
  cull: processSlicesRequestSchema.shape.cull,
  filter: processSlicesRequestSchema.shape.filter,
  inject_domesets: z
    .object({
      r2_retract: z.number().optional(),
      inset_set_period: z.number().int().optional(),
      init_dome_frac: z.number().optional(),
      final_dome_frac: z.number().optional(),
      strategy: z.enum(["flat", "linear", "power", "inverse_power", "bell"]).optional(),
      params: insetParamsSchema.optional(),
      plot_cross_section: z.boolean().optional(),
      save_cross_section: z.boolean().optional(),
      domain: z.array(z.number()).optional(),
    })
    .optional(),
  plunges_and_retracts: z
    .object({
      points_keys: z.array(z.string()),
      direction: z.union([z.array(z.array(z.number())), z.array(z.string())]),
      distance: z.array(z.number()).optional(),
      dist_for_safe: z.number().optional(),
      p2p: z.boolean().optional(),
      point_count: z.number().int().optional(),
      include_first_group: z.boolean().optional(),
      how_planar: z.number().optional(),
    })
    .optional(),
});

const addLinksRequestSchema = z.object({
  filter: processSlicesRequestSchema.shape.filter,
  orientations_from_axis_direction: z
    .object({
      axis_key: z.string().optional(),
      r2: z.boolean().optional(),
      rotate: z.boolean().optional(),
    })
    .optional(),
  add_p2p_links: z
    .object({
      points_keys: z.array(z.string()).optional(),
      link_dist_for_safe: z.number().optional(),
      z_safe_values: z.array(z.number()).optional(),
      trailing_keys: z.array(z.string()).optional(),
    })
    .optional(),
  add_dynamic_links: z
    .object({
      link_dist_for_safe: z.number().optional(),
    })
    .optional(),
});

const auxiliaryValuesRequestSchema = z.object({
  calc_e: z
    .object({
      offset: z.number().optional(),
      x_window: z.number().int().optional(),
      static_e: z.boolean().optional(),
      r1_gradual_shift: z.number().optional(),
      r2_gradual_shift: z.number().optional(),
      rail_range1: z.array(z.number()).nullable().optional(),
      rail_range2: z.array(z.number()).nullable().optional(),
    })
    .optional(),
  set_speeds_by_type: z
    .object({
      retract: z.number().optional(),
      plunge: z.number().optional(),
      link: z.number().optional(),
      form: z.number().optional(),
    })
    .optional(),
});

export function buildProcessSlicesRequest(values: PathPlanningFormValues) {
  return processSlicesRequestSchema.parse({
    ...(values.process_enable_cull
      ? {
          cull: {
            min_ptcount: values.process_cull_min_ptcount,
            cull_duplicates_by_layer: false,
            test_keys: ["r1_contact", "normal"],
            reindex: false,
          },
        }
      : {}),
    ...(values.process_enable_adaptive_resample
      ? {
          resample_adaptive: {
            points_key: "r1_contact",
            spacing: values.process_resample_spacing,
            ratio: 1,
            crv_method: "menger",
            max_angle: values.process_resample_max_angle,
            angle_tol: values.process_resample_angle_tol,
            do_filter: values.process_resample_do_filter,
            ...(values.process_resample_do_filter
              ? { strength: values.process_resample_strength }
              : {}),
            close: false,
          },
        }
      : {}),
    ...(values.process_enable_reduce_path
      ? {
          reduce_path: {
            e: values.process_reduce_tolerance,
            points_key: "r1_contact",
          },
        }
      : {}),
    parse_regions: {
      slice_dir: [0, 0, -1],
      slice_key: "r1_contact",
      parents_only: false,
      overwrite: false,
    },
    ...(values.process_enable_alternate_layers
      ? { alternate_layers: { odd: true, no_check: false } }
      : {}),
  });
}

export function buildToolAxesRequest(values: PathPlanningFormValues) {
  return toolAxesRequestSchema.parse({
    ...(values.process_enable_cull
      ? {
          cull: {
            min_ptcount: values.process_cull_min_ptcount,
            cull_duplicates_by_layer: false,
            test_keys: ["r1_contact", "normal"],
            reindex: false,
          },
        }
      : {}),
    ...(values.tool_axes_clamp_normals_enabled
      ? {
          clamp_normals: {
            upper_bound: values.tool_axes_clamp_normals_upper_bound,
            ...(typeof values.tool_axes_clamp_normals_lower_bound === "number"
              ? { lower_bound: values.tool_axes_clamp_normals_lower_bound }
              : {}),
            remap: values.tool_axes_clamp_normals_remap,
          },
        }
      : {}),
    generate_both_tips: {
      ...(typeof values.tool_axes_off_axis_r1 === "number" &&
      typeof values.tool_axes_off_axis_r2 === "number"
        ? { off_axis: [values.tool_axes_off_axis_r1, values.tool_axes_off_axis_r2] }
        : {}),
      fast_clamp_initial: values.tool_axes_fast_clamp_initial,
      sinelaw: true,
      powermill_vector: false,
      ...(values.tool_axes_axis_filter_strength != null
        ? { axis_filter_strength: values.tool_axes_axis_filter_strength }
        : {}),
    },
  });
}

function omitUndefined<T extends Record<string, unknown>>(record: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(record).filter(([, value]) => value !== undefined)
  ) as Partial<T>;
}

function buildInsetParams(values: PathPlanningFormValues): z.infer<typeof insetParamsSchema> {
  switch (values.optional_inset_strategy) {
    case "flat":
      return { spacing: values.optional_inset_flat_spacing };
    case "linear":
      return omitUndefined({
        slope: values.optional_inset_linear_slope,
        yshift: values.optional_inset_linear_yshift,
      });
    case "power":
    case "inverse_power":
      return omitUndefined({
        power: values.optional_inset_power_power,
        xshift: values.optional_inset_power_xshift,
        yshift: values.optional_inset_power_yshift,
        boundary_offset: values.optional_inset_power_boundary_offset,
      });
    case "bell":
      return omitUndefined({
        c: values.optional_inset_bell_c,
        w: values.optional_inset_bell_w,
        s: values.optional_inset_bell_s,
      });
  }
}

export function buildOptionalMovesRequest(values: PathPlanningFormValues) {
  return optionalMovesRequestSchema.parse({
    ...(values.process_enable_cull
      ? {
          cull: {
            min_ptcount: values.process_cull_min_ptcount,
            cull_duplicates_by_layer: false,
            test_keys: ["r1_contact", "normal"],
            reindex: false,
          },
        }
      : {}),
    ...(values.optional_enable_inject_domesets
      ? {
          inject_domesets: {
            r2_retract: values.optional_r2_retract,
            inset_set_period: values.optional_inset_set_period,
            init_dome_frac: values.optional_predome_init_frac,
            final_dome_frac: values.optional_predome_final_frac,
            strategy: values.optional_inset_strategy,
            params: buildInsetParams(values),
            plot_cross_section: false,
            save_cross_section: false,
          },
        }
      : {}),
    ...(values.optional_enable_plunges_retracts
      ? {
          plunges_and_retracts: {
            points_keys: ["r1_tip", "r2_tip"],
            direction: ["normal"],
            distance: [values.optional_plunge_distance, values.optional_retract_distance],
            dist_for_safe: values.links_link_dist_for_safe,
            p2p: true,
            point_count: 25,
            how_planar: values.optional_retract_angle,
          },
        }
      : {}),
  });
}

export function buildAddLinksRequest(values: PathPlanningFormValues) {
  return addLinksRequestSchema.parse({
    orientations_from_axis_direction: {
      axis_key: values.links_axis_key,
      r2: values.links_r2,
      rotate: values.links_rotate,
    },
    ...(!values.links_dynamic_links
      ? {
          add_p2p_links: {
            points_keys: ["r1_tip", "r2_tip"],
            link_dist_for_safe: values.links_link_dist_for_safe,
            z_safe_values: [values.links_r1_safe_plane, values.links_r2_safe_plane],
            trailing_keys: ["cut", "plunge", "retract", "link"],
          },
        }
      : {}),
    ...(values.links_dynamic_links
      ? {
          add_dynamic_links: {
            link_dist_for_safe: values.links_link_dist_for_safe,
          },
        }
      : {}),
  });
}

export function buildAuxiliaryValuesRequest(values: PathPlanningFormValues) {
  return auxiliaryValuesRequestSchema.parse({
    calc_e: {
      offset: values.auxiliary_rail_offset,
      static_e: !values.auxiliary_dynamic_rail,
      r1_gradual_shift: values.auxiliary_r1_shift,
      r2_gradual_shift: values.auxiliary_r2_shift,
      rail_range1:
        typeof values.auxiliary_r1_range_min === "number" &&
        typeof values.auxiliary_r1_range_max === "number"
          ? [values.auxiliary_r1_range_min, values.auxiliary_r1_range_max]
          : null,
      rail_range2:
        typeof values.auxiliary_r2_range_min === "number" &&
        typeof values.auxiliary_r2_range_max === "number"
          ? [values.auxiliary_r2_range_min, values.auxiliary_r2_range_max]
          : null,
    },
    ...(values.auxiliary_set_speeds
      ? {
          set_speeds_by_type: {
            retract: values.auxiliary_retract_speed,
            plunge: values.auxiliary_plunge_speed,
            link: values.auxiliary_link_speed,
            form: values.auxiliary_forming_speed,
          },
        }
      : {}),
  });
}
